import * as React from 'react'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import CloseIcon from '@mui/icons-material/Close'
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks'


import DateActiveImage from 'components/SimpleTable/assets/date-active.png'
import DateInactiveImage from 'components/SimpleTable/assets/date-inactive.png'
import PlainDate from 'lib/PlainDate'
import { dateRangeToTimeRange, timeRangeToDateRange } from 'lib/time'
import { convertToAbsoluteTimeRange } from 'components/DatePickerPopover/dateRangeUtils'
import { TimeRange } from 'components/DatePickerPopover/types'
import DatePickerPopover from 'components/DatePickerPopover/DatePickerPopover'
import colors from 'lib/colors'
import moment from 'moment'

export type LeadTableDateFilterValue = {
  operator: 'between'
  value: [PlainDate, PlainDate]
}

type LeadTableDateFilterProps = {
  title: string
  timezone: string
  value?: LeadTableDateFilterValue
  onChange: (value: LeadTableDateFilterValue | undefined) => void
}

function hasDateFilter(value?: LeadTableDateFilterValue) {
  return value?.operator === 'between' && Array.isArray(value.value) && value.value.length === 2
}

export default function LeadTableDateFilter({
  title,
  timezone,
  value,
  onChange,
}: LeadTableDateFilterProps) {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `lead-table-date-filter-${title}`,
  })

  const isActive = hasDateFilter(value)

  const handleClear = React.useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      event.stopPropagation()

      const today = PlainDate.from(moment.tz(timezone).format('YYYY-MM-DD'))
      onChange({
        operator: 'between',
        value: [today, today],
      })
    },
    [onChange, timezone]
  )

  const handleChange = React.useCallback(
    (timeRange: TimeRange) => {
      const absoluteTimeRange = convertToAbsoluteTimeRange(timeRange, timezone)
      const dateRange = timeRangeToDateRange(absoluteTimeRange)

      if (!dateRange) {
        onChange(undefined)
        return
      }

      onChange({
        operator: 'between',
        value: dateRange as [PlainDate, PlainDate],
      })
    },
    [onChange, timezone]
  )

  const initialValue = React.useMemo(() => {
    return dateRangeToTimeRange(value?.value ?? null, timezone)
  }, [value, timezone])

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        {...bindTrigger(popupState)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 'unset',
          position: 'relative',
          padding: '6px !important',
          borderColor: isActive ? colors.frenchBlue : colors.slateGray,
          '& .icon-layer': {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .clear-icon': {
            display: 'none',
          },
          '&:hover .filter-icon': {
            display: isActive ? 'none' : undefined,
          },
          '&:hover .clear-icon': {
            display: isActive ? 'flex' : 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 18,
            height: 18,
            flexShrink: 0,
          }}
        >
          <Box className='icon-layer filter-icon'>
            <Box
              component='img'
              src={DateInactiveImage}
              alt='inactive date filter icon'
              width={18}
              height={18}
              sx={{ display: isActive ? 'none' : 'block' }}
            />
            <Box
              component='img'
              src={DateActiveImage}
              alt='active date filter icon'
              width={18}
              height={18}
              sx={{
                display: isActive ? 'block' : 'none',
                position: 'absolute',
                inset: 0,
              }}
            />
          </Box>

          <Box className='icon-layer clear-icon'>
            <Tooltip title='Clear Filter'>
              <CloseIcon
                onClick={handleClear}
                fontSize='medium'
                aria-label={`Clear Filter ${title}`}
              />
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            height: '18px',
            pl: 0.5,
            fontSize: '12px',
            lineHeight: '18px',
            color: isActive ? colors.frenchBlue : colors.cadetGray,
          }}
        >
          {title}
        </Box>
      </Button>

      <DatePickerPopover
        {...bindPopover(popupState)}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onChange={handleChange}
        initialValue={initialValue}
      />
    </>
  )
}
